import './nav-shell.css'
import { Fragment, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Moon, PanelLeft, Search, Sun } from 'lucide-react'
import { useUiStore } from '@/store/ui.store'
import { useAuth } from '@/hooks/useAuth'
import { useModuleTheme } from '@/hooks/useModuleTheme'
import { usePageChrome } from '@/app/providers/PageChromeProvider'
import { resolvePageTitleFromPath } from '@/utils/page-title'
import { isMobileBackRoot, resolveMobileBackFallback } from '@/utils/home-path'
import { NotificationBell } from '@/components/common/NotificationBell'
import { MessengerIcon } from '@/components/common/MessengerIcon'
import { TaskReminderChip } from '@/components/common/TaskReminderChip'
import { CommandPalette } from './CommandPalette'
import { RailFloat } from './RailFloat'
import { DropdownMenu, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { NavAvatar, NavLogo, SidebarBody, UserMenuContent } from './SidebarBody'
import { useCommandPalette, useNavCrumbs, useNavModel, useRailFloat } from './useNavShell'

const FLIP_MS = 300
const FLIP_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'

function ThemeButton() {
  const themeMode = useUiStore((s) => s.theme)
  const toggleTheme = useUiStore((s) => s.toggleTheme)
  return (
    <button type="button" className="ns-iconbtn" aria-label="Toggle theme" onClick={toggleTheme}>
      {themeMode === 'light' ? <Moon /> : <Sun />}
    </button>
  )
}

/**
 * Desktop / tablet shell: flush frosted sidebar (expanded ↔ icon rail), light
 * top bar with breadcrumbs + page actions + utilities; `children` = page panel.
 */
export function DesktopNavShell({ children }: { children: ReactNode }) {
  const collapsed = useUiStore((s) => s.sidebarCollapsed)
  const toggleSidebar = useUiStore((s) => s.toggleSidebar)
  const { chrome } = usePageChrome()
  const theme = useModuleTheme(chrome.moduleId)
  const { entries, sections, pages, active } = useNavModel()
  const crumbs = useNavCrumbs(entries, active)
  const rail = useRailFloat(collapsed)
  const palette = useCommandPalette()
  const rootRef = useRef<HTMLDivElement>(null)
  const mainRef = useRef<HTMLDivElement>(null)
  const firstRun = useRef(true)

  // FLIP: the main column's left offset changes once (frame 0); translate it
  // back to where it was and glide to 0 in step with the sidebar clip-path.
  useLayoutEffect(() => {
    if (firstRun.current) {
      firstRun.current = false
      return
    }
    const el = mainRef.current
    const root = rootRef.current
    if (!el || !root || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const css = getComputedStyle(root)
    const delta = parseFloat(css.getPropertyValue('--ns-w')) - parseFloat(css.getPropertyValue('--ns-rail'))
    if (!Number.isFinite(delta) || delta <= 0) return
    const anim = el.animate(
      [{ transform: `translateX(${collapsed ? delta : -delta}px)` }, { transform: 'translateX(0)' }],
      { duration: FLIP_MS, easing: FLIP_EASE },
    )
    return () => anim.cancel()
  }, [collapsed])

  // Ctrl+\ toggles the sidebar.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault()
        toggleSidebar()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggleSidebar])

  const showPageActions = Boolean(chrome.actions || chrome.secondaryActions) && chrome.identityPlacement !== 'workspace'

  return (
    <div ref={rootRef} className="ns ns-desk absolute inset-0" data-collapsed={collapsed}>
      <aside className="ns-side" data-collapsed={collapsed} aria-label="Sidebar">
        <div className="ns-surface" />
        <div className="ns-edge" aria-hidden />
        <div className="ns-clip">
          <SidebarBody sections={sections} active={active} collapsed={collapsed} rail={rail} onSearch={palette.show} />
        </div>
      </aside>
      <RailFloat rail={rail} activeId={active.id} />
      <div ref={mainRef} className="ns-main">
        <header className="ns-top">
          <button
            type="button"
            className="ns-iconbtn"
            onClick={toggleSidebar}
            aria-label="Toggle navigation"
            aria-expanded={!collapsed}
            title="Toggle sidebar (Ctrl+\)"
          >
            <PanelLeft />
          </button>
          <nav className="ns-crumbs" aria-label="Breadcrumb">
            {crumbs.map((crumb, i) => (
              <Fragment key={`${crumb.label}-${i}`}>
                {i ? <ChevronRight className="ns-sep" aria-hidden /> : null}
                {i === crumbs.length - 1 ? (
                  <b aria-current="page">{crumb.label}</b>
                ) : crumb.href ? (
                  <Link to={crumb.href}>{crumb.label}</Link>
                ) : (
                  <span>{crumb.label}</span>
                )}
              </Fragment>
            ))}
          </nav>
          {showPageActions ? (
            <div className="ns-actions [&_button]:min-h-8 [&_button]:shrink-0 [&_button]:sm:min-h-9">
              {chrome.secondaryActions}
              {chrome.actions}
            </div>
          ) : null}
          <div className="ns-tools">
            <TaskReminderChip currentModuleId={theme.moduleId} />
            <MessengerIcon variant="header" />
            <NotificationBell variant="header" />
            <ThemeButton />
          </div>
        </header>
        <div className="ns-panel">{children}</div>
      </div>
      <CommandPalette palette={palette} pages={pages} />
    </div>
  )
}

/**
 * Phone top bar: back (sub-pages), logo, title, search, bell, avatar (account
 * menu: name/role, theme, logout, build). Navigation itself lives in the
 * bottom nav (module tabs, Add, Modules → Workspace hub).
 */
export function MobileTopBar() {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { chrome } = usePageChrome()
  const { pages } = useNavModel()
  const palette = useCommandPalette()
  const showBack = !isMobileBackRoot(pathname)
  const title = resolvePageTitleFromPath(pathname, search) ?? chrome.mobileTitle ?? chrome.title ?? ''

  const handleBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) navigate(-1)
    else navigate(resolveMobileBackFallback(pathname, user), { replace: true })
  }

  return (
    <div className="ns">
      <header className="ns-m-top" data-back={showBack}>
        {showBack ? (
          <button type="button" className="ns-iconbtn touch-target" aria-label="Go back" onClick={handleBack}>
            <ChevronLeft />
          </button>
        ) : null}
        <NavLogo />
        <h1>{title}</h1>
        <button type="button" className="ns-iconbtn touch-target" aria-label="Search pages" onClick={palette.show}>
          <Search />
        </button>
        <NotificationBell variant="mobile" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="ns-m-avatar touch-target" aria-label="Profile menu">
              <NavAvatar />
            </button>
          </DropdownMenuTrigger>
          <UserMenuContent side="bottom" align="end" signOut="none" />
        </DropdownMenu>
      </header>
      <CommandPalette palette={palette} pages={pages} />
    </div>
  )
}
